import type { FunctionalComponent } from 'vue'
import {
  Announcement02Icon,
  Briefcase02Icon,
  CalendarIcon,
  Cloud01Icon,
  Compass03Icon,
  FaceSmileIcon,
  File05Icon,
  Globe01Icon,
  Hash02Icon,
  HeartIcon,
  Home02Icon,
  Lightbulb02Icon,
  Lock01Icon,
  Mail01Icon,
  Pin02Icon,
  Rocket02Icon,
  Star05Icon,
  Trophy01Icon,
} from '@gohighlevel/ghl-icons/24/outline'

import { ChannelVisibility, type ChannelAccessInput, type IChannel } from '../types'

/**
 * Whether the current user can view content in this channel.
 * Public channels are always accessible; private channels require a membership role from the list API.
 */
export function hasUserChannelAccess(channel: ChannelAccessInput): boolean {
  if (channel.visibility !== ChannelVisibility.PRIVATE) {
    return true
  }

  return Boolean(channel.userRole)
}

/** Maps form visibility + discoverability toggle to the value sent on create/update access settings. */
export function resolveIsDiscoverable(visibility: ChannelVisibility, isDiscoverable?: boolean, isLevelUp2026Enabled = false): boolean {
  if (visibility === ChannelVisibility.PUBLIC) {
    return true
  }

  if (!isLevelUp2026Enabled) {
    return false
  }

  return !!isDiscoverable
}

/** Private channel that is not discoverable (a missing flag counts as hidden). */
export function isHiddenPrivateChannel(channel: Pick<Partial<IChannel>, 'visibility' | 'isDiscoverable'>): boolean {
  return channel.visibility === ChannelVisibility.PRIVATE && channel.isDiscoverable !== true
}

export type ChannelSettingsVisibilitySummaryKey = 'publicVisible' | 'privateVisible' | 'privateHidden'

/** About-tab label key suffix: Public-Visible, Private-Visible, or Private-Hidden. */
export function getChannelSettingsVisibilitySummaryKey(
  channel: Pick<IChannel, 'visibility' | 'isDiscoverable'>
): ChannelSettingsVisibilitySummaryKey {
  if (channel.visibility !== ChannelVisibility.PRIVATE) {
    return 'publicVisible'
  }

  return channel.isDiscoverable === true ? 'privateVisible' : 'privateHidden'
}

// ===== ICON CONFIGURATION =====

export const channelIcons = [
  { id: 1, iconName: 'FaceSmile', icon: FaceSmileIcon },
  { id: 2, iconName: 'Heart', icon: HeartIcon },
  { id: 3, iconName: 'Globe01', icon: Globe01Icon },
  { id: 4, iconName: 'Home02', icon: Home02Icon },
  { id: 5, iconName: 'Announcement02', icon: Announcement02Icon },
  { id: 6, iconName: 'Calendar', icon: CalendarIcon },
  { id: 7, iconName: 'Rocket02', icon: Rocket02Icon },
  { id: 8, iconName: 'File05', icon: File05Icon },
  { id: 9, iconName: 'Trophy01', icon: Trophy01Icon },
  { id: 10, iconName: 'Briefcase02', icon: Briefcase02Icon },
  { id: 11, iconName: 'Lock01Icon', icon: Lock01Icon },
  { id: 12, iconName: 'Lightbulb02', icon: Lightbulb02Icon },
  { id: 13, iconName: 'Mail01', icon: Mail01Icon },
  { id: 14, iconName: 'Pin02', icon: Pin02Icon },
  { id: 15, iconName: 'Cloud01', icon: Cloud01Icon },
  { id: 16, iconName: 'Star05', icon: Star05Icon },
  { id: 17, iconName: 'Compass03', icon: Compass03Icon },
  { id: 18, iconName: 'Hash02Icon', icon: Hash02Icon },
]

// ===== ICON HELPER FUNCTIONS =====

/**
 * Check if a string is an emoji
 * @param icon The icon string to check
 * @returns True if the string is an emoji
 */
export function isChannelIconEmoji(icon?: string): boolean {
  if (!icon) return false
  // Emoji detection: allow variation selectors (e.g. 🗝️ = key + U+FE0F), so length can be 3–4 code units
  const emojiRanges =
    /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F100}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u
  return emojiRanges.test(icon.trim()) && icon.length <= 8
}

/**
 * Get channel icon component by name
 * @param iconName The icon name to look up
 * @returns The icon component or undefined
 */
export function getChannelIcon(iconName: string): FunctionalComponent<Record<string, unknown>> | undefined {
  const icon = channelIcons.find(icon => icon.iconName === iconName)
  return icon?.icon
}

/**
 * Check if an icon name exists in the icons configuration
 * @param iconName The icon name to check
 * @returns True if the icon exists
 */
export function hasChannelIcon(iconName: string): boolean {
  return channelIcons.some(icon => icon.iconName === iconName)
}

/**
 * Get channel emoji based on type
 * @param channel The channel object (partial is acceptable since only icon is used)
 * @returns The appropriate emoji for the channel type
 */
export function getChannelEmojiByType(channel: Partial<IChannel>): string | undefined {
  // Handle Home tab specially

  // Use custom icon if it's an emoji
  if (channel.icon && isChannelIconEmoji(channel.icon)) {
    return channel.icon
  }
}
